import { all, call, put, takeLatest } from 'redux-saga/effects';
import { PromiseType } from 'utility-types';

import workspaceService from '@app/libs/workspaceService';
import { SIGNING_PROGRESS_INFORMATION__FETCH_REQUEST } from '@app/store/actionTypes';
import Document from '@app/store/document/Document';
import {
  fetchSigningProgressInformation,
  fetchSigningProgressInformationFailure,
  fetchSigningProgressInformationSuccess,
  ParticipantAuth,
} from '@app/store/signingProgressInformation/actions';
import History from '@app/store/signingProgressInformation/History';
import { getDocumentsByIdDeprecated } from '@app/store/transport-layer/document/documents';
import { getDocumentsAttachmentsDeprecated } from '@app/store/transport-layer/document/documents/attachments';
import { getDocumentsHistories } from '@app/store/transport-layer/document/documents/histories';
import {
  getDocumentsParticipantAuths,
  ParticipantAuthentication,
} from '@app/store/transport-layer/document/documents/participant-auths';

export async function getSigningProgressInformation({
  workspaceId,
  documentId,
}: {
  workspaceId: string;
  documentId: string;
}) {
  const document = await getDocumentsByIdDeprecated({ workspaceId, documentId });

  const [participantAuthsResult, documentHistoriesResult, attachmentResult] =
    await Promise.allSettled([
      getDocumentsParticipantAuths({ workspaceId, documentId }),
      getDocumentsHistories({ workspaceId, documentId }),
      getDocumentsAttachmentsDeprecated({ workspaceId, documentId }),
    ]);

  // FIXME: 일부실패는 mock 데이터로 대체함. 각 쿼리에 대한 에러처리가 UI로 나타나지 않음.
  return [
    document,
    participantAuthsResult.status === 'fulfilled' ? participantAuthsResult.value : [],
    documentHistoriesResult.status === 'fulfilled' ? documentHistoriesResult.value : [],
    attachmentResult.status === 'fulfilled'
      ? attachmentResult.value
      : { attachments: [], requesterAttachments: [] },
  ];
}

export function* fetchSigningProgressInformationSaga({
  payload: { documentId },
}: ReturnType<typeof fetchSigningProgressInformation>) {
  try {
    const workspaceId = workspaceService.getWorkspaceId();

    const [document, participantAuthsResponse, histories, { attachments, requesterAttachments }]: [
      Document,
      ParticipantAuthentication[],
      History[],
      PromiseType<ReturnType<typeof getDocumentsAttachmentsDeprecated>>,
    ] = yield call(getSigningProgressInformation, { workspaceId, documentId });

    // 전송계층에서 전달된 인증 정보를 도메인에 맞춰 변경
    const participantAuths: ParticipantAuth[] = participantAuthsResponse.map(
      (participantAuthentication) => {
        const transformedParticipantAuthentication: ParticipantAuth = {
          digitalCertificate: null,
          mobile: null,
          password: false,
          participantId: participantAuthentication.participantId,
        };

        participantAuthentication.auths.forEach((auth) => {
          const splitedAuthValue = auth.authValue?.split('|');
          if (auth.authType === 'D_CERT' && splitedAuthValue && splitedAuthValue.length === 2) {
            transformedParticipantAuthentication.digitalCertificate = {
              bizNumber: splitedAuthValue[1],
              companyName: splitedAuthValue[0],
            };
          }
          if (
            auth.authType === 'MOBILE_IDENTIFICATION' &&
            splitedAuthValue &&
            splitedAuthValue.length === 2
          ) {
            transformedParticipantAuthentication.mobile = {
              phoneNumber: splitedAuthValue[1],
              realName: splitedAuthValue[0],
            };
          }
          if (auth.authType === 'PASSWORD') {
            transformedParticipantAuthentication.password = true;
          }
        });

        return transformedParticipantAuthentication;
      }
    );

    yield put(
      fetchSigningProgressInformationSuccess({
        attachments,
        document,
        histories,
        participantAuths,
        requesterAttachments,
      })
    );
  } catch (error) {
    yield put(fetchSigningProgressInformationFailure(error));
  }
}

export default function* sagas() {
  yield all([
    takeLatest(SIGNING_PROGRESS_INFORMATION__FETCH_REQUEST, fetchSigningProgressInformationSaga),
  ]);
}
